// frontend/src/pages/PublicProfile.js
// Build: 2026-02-19-public-profile-v8 (Premium Author Page)
//
// Goals:
// - /u/:username renders a premium Author Page (matches Profile.js premium hero + shelf)
// - No editing, no private fields, no noise
// - Canon count displayed correctly using story-level counters when present
// - Uses GET /api/users/:username/profile
//
// Notes:
// - App.js route already points /u/* here (confirmed by your grep).
// - This file is intentionally self-contained and does not depend on Profile.js state.

import React, { useEffect, useMemo, useRef, useState } from "react";
import "../App.css";
import { api } from "../api";
import StoryIdentityPlate from "../components/StoryIdentityPlate";
import { getPublicationInfo } from "../utils/publicationStatus";

export default function PublicProfile({ onNavigatePage }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [loaded, setLoaded] = useState(false);

  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [storylines, setStorylines] = useState([]);

  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  function safeTrim(v) {
    return String(v || "").trim();
  }

  function safeArray(v) {
    return Array.isArray(v) ? v : [];
  }

  function parsePublicUsernameFromPath() {
    try {
      const p = safeTrim(window?.location?.pathname || "");
      const parts = p.split("/").filter(Boolean);
      const idx = parts.indexOf("u");
      if (idx >= 0 && parts[idx + 1]) return safeTrim(parts[idx + 1]).toLowerCase();
      if (parts.length >= 2 && parts[0].toLowerCase() === "u") return safeTrim(parts[1]).toLowerCase();
      return "";
    } catch {
      return "";
    }
  }

  function clampInt(n, min, max, fallback) {
    const x = Number(n);
    if (!Number.isFinite(x)) return fallback;
    const v = Math.floor(x);
    if (v < min) return min;
    if (v > max) return max;
    return v;
  }

  function inferCanonCount(story) {
    const candidates = [
      story?.canonCount,
      story?.canonScenes,
      story?.canonSceneCount,
      story?.canonApprovedCount,
      story?.canonApproved,
      story?.canonTotal,
      story?.canon,
      story?.canonWritten,
    ];

    for (const c of candidates) {
      const n = Number(c);
      if (Number.isFinite(n)) return Math.max(0, Math.floor(n));
    }

    const scenes = safeArray(story?.scenes);
    if (scenes.length) {
      const approved = scenes.filter((s) => s && (s.isCanon === true || s.status === "canon" || s.approved === true));
      return approved.length;
    }

    return 0;
  }

  function fmtDateTime(raw) {
    try {
      const d = raw ? new Date(raw) : null;
      if (!d || Number.isNaN(d.getTime())) return "—";
      return d.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "—";
    }
  }

  function normalizeUrl(raw) {
    const v = safeTrim(raw);
    if (!v) return "";
    if (/^https?:\/\//i.test(v)) return v;
    return `https://${v}`;
  }

  function pillStyle({ tone = "soft", strong = false } = {}) {
    const base = {
      padding: "7px 12px",
      borderRadius: 999,
      fontWeight: strong ? 950 : 850,
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      whiteSpace: "nowrap",
      lineHeight: 1,
    };

    if (tone === "ink") {
      return { ...base, background: "#0f172a", color: "#ffffff", border: "1px solid #0f172a" };
    }
    if (tone === "paper") {
      return { ...base, background: "#f6f2ea", color: "#0f172a", border: "1px solid #eadfce" };
    }
    return { ...base, background: "#ffffff", color: "#0f172a", border: "1px solid #e5e7eb" };
  }

  function subtleCardStyle() {
    return { border: "1px solid #e5e7eb", borderRadius: 20, padding: 16, background: "#fff" };
  }

  function normalizePublicShelfTitle(value) {
    return String(value || "")
      .toLowerCase()
      .replace(/[’']/g, "")
      .replace(/[^a-z0-9àâçéèêëîïôûùüÿñæœ\s-]/gi, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  function isPublicShelfStatusPublished(story) {
    const raw = String(story?.publicationStatus || story?.publishingStatus || "").trim().toLowerCase();
    return (
      raw === "publishedamazon" ||
      raw === "published_amazon" ||
      raw === "published" ||
      raw === "amazon" ||
      raw === "completed" ||
      raw === "complete" ||
      raw === "publishedexternal" ||
      raw === "published_external" ||
      raw === "external"
    );
  }

  function isKnownPublicShelfPublishedBook(story) {
    const title = normalizePublicShelfTitle(story?.title || story?.publishedTitle || story?.publicationTitle);

    return (
      title.includes("reflections of the wayfarer") ||
      title.includes("reflections of wayfarer") ||
      title.includes("réflexions du voyageur") ||
      title.includes("reflexions du voyageur") ||
      title.includes("réflexions dun voyageur") ||
      title.includes("reflexions dun voyageur") ||
      title.includes("réflexions d un voyageur") ||
      title.includes("reflexions d un voyageur") ||
      title.includes("ugo and her pet dog") ||
      title.includes("ugo et son petit chien") ||
      title.includes("ugo and the butterfly woman") ||
      title.includes("ugo et la femme papillon") ||
      title.includes("ugo et la femme-papillon")
    );
  }

  function getPublicShelfPublicationInfo(story, lang = "en") {
    const known = isKnownPublicShelfPublishedBook(story);
    const alreadyPublished = isPublicShelfStatusPublished(story);
    const storyForPublication = known && !alreadyPublished
      ? { ...(story || {}), publicationStatus: "publishedAmazon" }
      : story;

    return getPublicationInfo(storyForPublication, lang);
  }

  const viewingUsername = useMemo(() => parsePublicUsernameFromPath(), [window?.location?.pathname]);

  const displayName = useMemo(() => {
    const n = safeTrim(profile?.name);
    const u = safeTrim(profile?.username || viewingUsername);
    return n || u || "Unknown";
  }, [profile, viewingUsername]);

  const handle = useMemo(() => {
    const u = safeTrim(profile?.username || viewingUsername);
    return u ? `@${u}` : "@unknown";
  }, [profile, viewingUsername]);

  const initials = useMemo(() => {
    const u = safeTrim(profile?.username || viewingUsername);
    const n = safeTrim(profile?.name);
    const base = (n || u || "U").trim();
    const parts = base.split(" ").filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return base.slice(0, 1).toUpperCase();
  }, [profile, viewingUsername]);

  const roleLabel = useMemo(() => String(profile?.role || "user").toLowerCase(), [profile]);

  const websiteUrl = useMemo(() => normalizeUrl(profile?.website), [profile]);
  const xLink = useMemo(() => normalizeUrl(profile?.xUrl), [profile]);
  const instagramLink = useMemo(() => normalizeUrl(profile?.instagramUrl), [profile]);
  const hasLinks = Boolean(websiteUrl || xLink || instagramLink);

  const statTile = (label, value) => (
    <div className="ns-stat-tile">
      <div className="ns-stat-label">{label}</div>
      <div className="ns-stat-value">{clampInt(value || 0, 0, 999999999, 0)}</div>
    </div>
  );

  function openStory(st) {
    const id = st?.id || st?._id;
    if (!id) return;

    if (onNavigatePage) {
      onNavigatePage("readerView", { storyId: id });
      return;
    }

    window.location.href = `/reader-view?storyId=${encodeURIComponent(id)}`;
  }

  async function loadPublicProfile(uName) {
    const u = safeTrim(uName).toLowerCase();
    if (!u) {
      setErr("No username found in URL.");
      setLoaded(true);
      return;
    }

    setBusy(true);
    setErr("");
    setOk("");

    try {
      const data = await api(`/api/users/${encodeURIComponent(u)}/profile`, { method: "GET" });
      if (!aliveRef.current) return;

      setProfile(data?.user || null);
      setStats(data?.stats || null);
      setStorylines(Array.isArray(data?.storylines) ? data.storylines : []);
      setLoaded(true);
    } catch (e) {
      if (!aliveRef.current) return;
      setErr(
        e?.message ||
          "Public profile could not be loaded. If you just added public profiles, make sure the backend endpoint is deployed."
      );
      setProfile(null);
      setStats(null);
      setStorylines([]);
      setLoaded(true);
    } finally {
      if (!aliveRef.current) return;
      setBusy(false);
    }
  }

  useEffect(() => {
    loadPublicProfile(viewingUsername);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewingUsername]);

  return (
    <div className="page ns-profile-page">
      <div className="card" style={{ maxWidth: 1120, margin: "0 auto" }}>
        {/* Shared NS Hero */}
        <header
          className="ns-identity-hero ns-public-profile-hero"
          style={{
            backgroundImage: `linear-gradient(90deg, rgba(4, 31, 24, 0.94) 0%, rgba(4, 47, 34, 0.84) 48%, rgba(4, 31, 24, 0.68) 100%), url(${process.env.PUBLIC_URL}/images/storyhub/storyhub-background.png)`,
          }}
        >
          <div className="ns-identity-hero-copy">
            <p
              className="ns-identity-kicker"
              style={{
                color: "#D9A441",
                borderColor: "rgba(217, 164, 65, 0.52)",
                background: "rgba(217, 164, 65, 0.10)",
                textShadow: "0 1px 10px rgba(217, 164, 65, 0.18)",
              }}
            >
              NEXTSCENES
            </p>
            <h1 className="ns-identity-title">Author Page</h1>
            <p className="ns-identity-subtitle">Public profile only. No private account fields, no editing, no noise.</p>

            <div className="ns-identity-actions">
              <span className="ns-card-meta ns-identity-status">Public view</span>
              {viewingUsername ? <span className="ns-card-meta ns-identity-status">@{viewingUsername}</span> : null}

              <button
                className="ns-button ns-button-outline"
                type="button"
                onClick={() => loadPublicProfile(viewingUsername)}
                disabled={busy}
              >
                {busy ? "Working..." : "Refresh"}
              </button>

              <button
                className="ns-button ns-button-outline"
                type="button"
                onClick={() => (onNavigatePage ? onNavigatePage("home") : (window.location.href = "/"))}
              >
                Go Home
              </button>
            </div>
          </div>

          <div className="ns-identity-hero-stat" aria-label="Author summary">
            <span className="ns-identity-stat-number">
              {clampInt(stats?.storylinesInitiated || storylines.length, 0, 999999, 0)}
            </span>
            <span className="ns-identity-stat-label">
              {clampInt(stats?.storylinesInitiated || storylines.length, 0, 999999, 0) === 1 ? "public storyline" : "public storylines"}
            </span>
          </div>
        </header>

        {err ? (
          <div className="banner" style={{ marginTop: 12 }}>
            {err}
          </div>
        ) : null}

        {ok ? (
          <div className="banner" style={{ marginTop: 12 }}>
            {ok}
          </div>
        ) : null}

        {!loaded ? (
          <div style={{ marginTop: 14, color: "#475569" }}>Loading…</div>
        ) : !profile ? (
          <div style={{ marginTop: 14, color: "#475569" }}>
            No profile data.
            <div style={{ marginTop: 10, fontSize: 12, color: "#64748b" }}>
              Backend must support{" "}
              <span style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" }}>
                GET /api/users/:username/profile
              </span>
              .
            </div>
          </div>
        ) : (
          <>
            {/* Author Hero */}
            <div className="ns-author-hero" style={{ marginTop: 16 }}>
              <div className="ns-author-hero-inner">
                <div className="ns-author-avatar" title="Avatar">
                  {safeTrim(profile?.avatarUrl) ? (
                    <img
                      src={safeTrim(profile.avatarUrl)}
                      alt="avatar"
                      className="ns-author-avatar-img"
                      onError={(e) => {
                        try {
                          e.currentTarget.style.display = "none";
                        } catch {}
                      }}
                    />
                  ) : (
                    <span className="ns-author-avatar-initials">{initials}</span>
                  )}
                </div>

                <div className="ns-author-meta">
                  <div className="ns-author-name-row">
                    <div className="ns-author-name">{displayName}</div>
                    <span className="ns-author-role">{roleLabel}</span>
                  </div>

                  <div className="ns-author-handle">{handle}</div>

                  <div className="ns-author-badges">
                    <span style={pillStyle({ tone: "paper", strong: true })}>NextScenes Author</span>
                    {safeTrim(profile?.location) ? <span style={pillStyle({ tone: "soft" })}>{safeTrim(profile.location)}</span> : null}
                    <span style={pillStyle({ tone: "soft" })}>Public view</span>
                  </div>

                  <div className="ns-author-bio">
                    {safeTrim(profile?.bio) ? (
                      <span style={{ whiteSpace: "pre-wrap" }}>{safeTrim(profile.bio)}</span>
                    ) : (
                      <span style={{ color: "#64748b", fontWeight: 750 }}>No public bio yet.</span>
                    )}
                  </div>

                  {hasLinks ? (
                    <div className="ns-author-links">
                      {websiteUrl ? (
                        <a className="ns-link-chip" href={websiteUrl} target="_blank" rel="noreferrer" title="Website">
                          Website
                        </a>
                      ) : null}
                      {xLink ? (
                        <a className="ns-link-chip" href={xLink} target="_blank" rel="noreferrer" title="X">
                          X
                        </a>
                      ) : null}
                      {instagramLink ? (
                        <a className="ns-link-chip" href={instagramLink} target="_blank" rel="noreferrer" title="Instagram">
                          Instagram
                        </a>
                      ) : null}
                    </div>
                  ) : (
                    <div className="ns-author-links" style={{ color: "#64748b", fontWeight: 750 }}>
                      No public links.
                    </div>
                  )}
                </div>

                <div className="ns-author-stats">
                  {statTile("Storylines initiated", stats?.storylinesInitiated)}
                  {statTile("Canon scenes written", stats?.canonScenesWritten)}
                </div>
              </div>
            </div>

            {/* Shelf */}
            <div className="ns-premium-shelf" style={{ marginTop: 14, ...subtleCardStyle(), padding: 18 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                  alignItems: "baseline",
                }}
              >
                <div>
                  <h3 style={{ marginTop: 0, marginBottom: 6 }}>Public Shelf</h3>
                  <p style={{ marginTop: 0, color: "#475569" }}>A serious writer is measured by pages, not speeches. These are the pages.</p>
                </div>

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                  <span style={pillStyle({ tone: "paper" })}>{Array.isArray(storylines) ? storylines.length : 0} storylines</span>
                </div>
              </div>

              {!Array.isArray(storylines) || storylines.length === 0 ? (
                <div style={{ color: "#64748b", fontWeight: 750 }}>No public storylines to show yet.</div>
              ) : (
                <div className="ns-shelf-grid-premium">
                  {storylines.map((st) => {
                    const key = st.id || st._id || `${st.title || "story"}-${Math.random().toString(36).slice(2)}`;
                    const forum = safeTrim(st?.forum);
                    const mode = safeTrim(st?.writingMode);

                    const publicationInfo = getPublicShelfPublicationInfo(st, "en");
                    const isPublishedBook = Boolean(publicationInfo?.isPublished);
                    const publishedTitle = safeTrim(publicationInfo?.publishedTitle || st?.publishedTitle || st?.title || "Published Book");
                    const publishedAuthor = safeTrim(publicationInfo?.publishedAuthorName || st?.publishedAuthorName || st?.publicationAuthorName || st?.authorName || "Goddy Oguzie");
                    const publishedCover = safeTrim(publicationInfo?.coverUrl);
                    const publishedGenre = forum || safeTrim(st?.category) || safeTrim(st?.genre);

                    const canonCount = inferCanonCount(st);
                    const lastUpdated = st?.updatedAt || st?.lastUpdated || st?.last_updated || st?.modifiedAt || "";
                    const created = st?.createdAt || st?.created_at || "";

                    return (
                      <div key={key} className={`ns-shelf-card-premium${isPublishedBook ? " is-published-book" : ""}`}>
                        <button className="ns-shelf-plateBtn" type="button" onClick={() => openStory(st)} title={isPublishedBook ? "View Published Book" : "Open story"}>
                          {isPublishedBook && publishedCover ? (
                            <img
                              src={publishedCover}
                              alt={publishedTitle}
                              className="ns-public-shelf-published-cover"
                              loading="lazy"
                            />
                          ) : (
                            <StoryIdentityPlate story={st} width={156} height={196} showFooter={true} />
                          )}
                        </button>

                        <div className="ns-shelf-meta-premium">
                          {isPublishedBook ? (
                            <div className="ns-public-shelf-published-info">
                              <div className="ns-public-shelf-published-title">{publishedTitle}</div>
                              {publishedAuthor ? (
                                <div className="ns-public-shelf-published-author">By <strong>{publishedAuthor}</strong></div>
                              ) : null}
                              {publishedGenre ? <div className="ns-public-shelf-published-genre">{publishedGenre}</div> : null}
                              <div className="ns-public-shelf-published-badges">
                                <span className="ns-publication-chip is-published">{publicationInfo?.publicationLabel || "Published on Amazon"}</span>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="ns-shelf-toprow">
                                <div className="ns-shelf-tags-premium">
                                  {forum ? <span className="ns-tag">{forum}</span> : null}
                                  {mode ? <span className="ns-tag">{mode}</span> : null}
                                  <span className="ns-tag ns-tag-ink">{clampInt(canonCount, 0, 999999, 0)} canon</span>
                                </div>
                              </div>

                              <div className="ns-shelf-dates">
                                <div className="ns-shelf-dateRow">
                                  <span className="ns-shelf-dateLabel">Last updated</span>
                                  <span className="ns-shelf-dateValue">{fmtDateTime(lastUpdated)}</span>
                                </div>
                                <div className="ns-shelf-dateRow">
                                  <span className="ns-shelf-dateLabel">Created</span>
                                  <span className="ns-shelf-dateValue">{fmtDateTime(created)}</span>
                                </div>
                              </div>
                            </>
                          )}

                          <div className="ns-shelf-actions-premium">
                            <button className="btn" type="button" onClick={() => openStory(st)}>
                              {isPublishedBook ? "View Published Book" : "Open"}
                            </button>

                            {isPublishedBook && publicationInfo?.amazonHref ? (
                              <button
                                className="btn ns-btn-soft ns-amazon-action"
                                type="button"
                                onClick={() => window.open(publicationInfo.amazonHref, "_blank", "noopener,noreferrer")}
                              >View on Amazon</button>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <style>{`
              .ns-author-hero {
                border-radius: 22px;
                border: 1px solid #eadfce;
                background: #f6f2ea;
                overflow: hidden;
              }

              .ns-author-hero-inner {
                display: grid;
                grid-template-columns: 96px 1fr 260px;
                gap: 16px;
                padding: 16px;
                align-items: start;
              }

              .ns-author-avatar {
                width: 86px;
                height: 86px;
                border-radius: 20px;
                border: 1px solid rgba(15, 23, 42, 0.12);
                background: rgba(255, 255, 255, 0.7);
                overflow: hidden;
                display: flex;
                align-items: center;
                justify-content: center;
              }

              .ns-author-avatar-img {
                width: 100%;
                height: 100%;
                object-fit: cover;
                display: block;
              }

              .ns-author-avatar-initials {
                font-weight: 950;
                font-size: 22px;
                color: #0f172a;
                letter-spacing: 0.5px;
              }

              .ns-author-meta {
                min-width: 240px;
              }

              .ns-author-name-row {
                display: flex;
                gap: 10px;
                align-items: center;
                flex-wrap: wrap;
              }

              .ns-author-name {
                font-weight: 950;
                font-size: 26px;
                color: #0f172a;
                line-height: 1.15;
              }

              .ns-author-role {
                padding: 6px 10px;
                border-radius: 999px;
                background: #0f172a;
                color: #ffffff;
                font-weight: 900;
                font-size: 12px;
                letter-spacing: 0.2px;
                text-transform: lowercase;
              }

              .ns-author-handle {
                margin-top: 4px;
                color: #475569;
                font-weight: 850;
              }

              .ns-author-badges {
                margin-top: 10px;
                display: flex;
                gap: 10px;
                flex-wrap: wrap;
              }

              .ns-author-bio {
                margin-top: 12px;
                color: #0f172a;
                font-weight: 750;
                line-height: 1.55;
              }

              .ns-author-links {
                margin-top: 10px;
                display: flex;
                gap: 10px;
                flex-wrap: wrap;
                align-items: center;
              }

              .ns-link-chip {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                padding: 8px 12px;
                border-radius: 999px;
                border: 1px solid rgba(15, 23, 42, 0.16);
                background: rgba(255, 255, 255, 0.75);
                color: #0f172a;
                font-weight: 850;
                text-decoration: none;
              }

              .ns-link-chip:hover {
                text-decoration: underline;
              }

              .ns-author-stats {
                display: grid;
                grid-template-columns: 1fr;
                gap: 12px;
                align-content: start;
              }

              .ns-stat-tile {
                border: 1px solid rgba(15, 23, 42, 0.12);
                border-radius: 18px;
                padding: 14px;
                background: rgba(255, 255, 255, 0.78);
              }

              .ns-stat-label {
                font-size: 12px;
                color: #64748b;
                font-weight: 850;
              }

              .ns-stat-value {
                margin-top: 6px;
                font-weight: 950;
                font-size: 20px;
                color: #0f172a;
              }

              .ns-premium-shelf {
                background: linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,250,252,0.55) 100%);
              }

              .ns-shelf-grid-premium {
                margin-top: 14px;
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 14px;
              }

              .ns-shelf-card-premium {
                border: 1px solid rgba(15, 23, 42, 0.10);
                border-radius: 22px;
                background: #ffffff;
                padding: 14px;
                display: grid;
                grid-template-columns: 170px 1fr;
                gap: 14px;
                align-items: stretch;
                box-shadow: 0 10px 24px rgba(2, 6, 23, 0.06);
              }

              .ns-shelf-plateBtn {
                width: 170px;
                padding: 0;
                border: 0;
                background: transparent;
                cursor: pointer;
                display: flex;
                align-items: flex-start;
              }

              .ns-shelf-meta-premium {
                min-width: 220px;
                display: flex;
                flex-direction: column;
                gap: 10px;
              }

              .ns-shelf-toprow {
                display: flex;
                justify-content: space-between;
                gap: 10px;
                align-items: flex-start;
                flex-wrap: wrap;
              }

              .ns-shelf-tags-premium {
                display: flex;
                gap: 10px;
                flex-wrap: wrap;
                align-items: center;
              }

              .ns-tag {
                padding: 6px 10px;
                border-radius: 999px;
                border: 1px solid #e5e7eb;
                font-weight: 850;
                color: #0f172a;
                background: #ffffff;
              }

              .ns-tag-ink {
                border-color: rgba(15, 23, 42, 0.14);
                background: rgba(15, 23, 42, 0.06);
              }

              .ns-shelf-dates {
                border: 1px solid rgba(15, 23, 42, 0.10);
                border-radius: 16px;
                background: rgba(248,250,252,0.75);
                padding: 10px 12px;
              }

              .ns-shelf-dateRow {
                display: flex;
                justify-content: space-between;
                gap: 12px;
                padding: 6px 0;
                border-bottom: 1px solid rgba(15, 23, 42, 0.07);
              }

              .ns-shelf-dateRow:last-child {
                border-bottom: none;
              }

              .ns-shelf-dateLabel {
                color: #64748b;
                font-weight: 850;
                font-size: 12px;
              }

              .ns-shelf-dateValue {
                color: #0f172a;
                font-weight: 900;
                font-size: 12px;
                text-align: right;
              }

              .ns-shelf-actions-premium {
                display: flex;
                gap: 10px;
                flex-wrap: wrap;
                margin-top: auto;
              }

              @media (max-width: 980px) {
                .ns-author-hero-inner {
                  grid-template-columns: 86px 1fr;
                }
                .ns-author-stats {
                  grid-column: 1 / -1;
                  grid-template-columns: 1fr 1fr;
                }
                .ns-shelf-grid-premium {
                  grid-template-columns: 1fr;
                }
              }

              @media (max-width: 520px) {
                .ns-author-name {
                  font-size: 22px;
                }
                .ns-author-stats {
                  grid-template-columns: 1fr;
                }
                .ns-shelf-card-premium {
                  grid-template-columns: 1fr;
                }
                .ns-shelf-plateBtn {
                  width: 100%;
                }
              }
            `}</style>
          </>
        )}
      </div>
    </div>
  );
}
